import { isAxiosError } from 'axios';
import TripPriceClass from '../TripPriceClass';
import { PaxTypeTitles } from '../TripModuleTypes';
import RouteBillHandler from './RouteBillHandler';
import {
    ItineraryVerifyException,
    VerificationStatus,
} from '@itinerary/Exceptions';
import { PaxType } from '@utils/types';
import { type PassengerCount, type PassengerFare } from '@itinerary/types';
import VerifyResult from '@itinerary/VerifyResult';
import { type InvoiceEntry } from '@store/invoice';
import PassengerForm from '@itinerary/PassengerForm';
import { PassengerFields } from '@store/form';
import { JourneyApiApiReservePost } from '@clients/MultiRouteApi';
import { type SellType } from '@utils/train';

export default class RoutePriceClass extends TripPriceClass {
    override showCapacity = true;

    override capacityText = 'مسافر';

    cancellationTerms = '';

    routePriceClasses: TripPriceClass[] = [];
    packageId: null | string = null;
    maxCapacity: number | undefined;

    classes = [];

    get sellType(): SellType {
        return this.routePriceClasses.find(
            priceClass => 'sellType' in priceClass
        )?.sellType as SellType;
    }

    constructor({
        routePriceClasses = [],
        packageId,
        maxCapacity,
        termsAndConditions,
        adultPrice,
    }: {
        routePriceClasses: TripPriceClass[];
        packageId: string;
        maxCapacity: number;
        adultPrice: number;
        termsAndConditions?: {
            term: string;
            destination: string;
            origin: string;
        }[];
    }) {
        super();
        this.maxCapacity = maxCapacity;
        this.routePriceClasses = routePriceClasses;
        this.packageId = packageId;
        const staticText =
            'این سفر شامل چند بلیط جداگانه است. تغییر زمان، کنسلی یا جاماندن از هر بلیط، باعث تغییر برنامه یا کنسلی بدون جریمه بقیه نمی‌شود و کنسلی سایر بلیط‌ها مشمول جریمه است. انتخاب این سفر به منزله پذیرش شرایط است.';
        this.cancellationTerms = `${staticText} <br /> ${
            termsAndConditions
                ?.map(
                    ({ destination, origin, term }) =>
                        `${origin} به ${destination}: ${term}`
                )
                ?.join('<br />') ?? ''
        }`;
        this.passengerFares = [
            {
                paxType: PaxType.Adult,
                price: adultPrice,
                discount: 0,
            },
        ];
    }

    override passengerFares: PassengerFare[] = [];

    private createDefaultForm(paxType: PaxType) {
        return new PassengerForm({
            type: paxType,
            title: PaxTypeTitles[paxType],
            defaultFields: PassengerFields.None,
            foreignerExtraFields: PassengerFields.None,
            iranianExtraFields: PassengerFields.None,
        });
    }

    loadBaggage() {}

    private filterForm(form: PassengerForm) {
        return new PassengerForm({
            ...form,
            defaultFields: form.defaultFields & ~PassengerFields.Nationality,
        });
    }

    override createPassengerForms(reserveData?: any): {
        adultForm: PassengerForm;
        childForm: PassengerForm;
        infantForm: PassengerForm;
    } {
        return this.routePriceClasses.reduce(
            (journeyForms, tripPriceClass) => {
                const tripForms =
                    tripPriceClass.createPassengerForms(reserveData);
                return {
                    adultForm: this.filterForm(
                        journeyForms.adultForm.mergeFields(tripForms.adultForm)
                    ),
                    childForm: this.filterForm(
                        journeyForms.childForm.mergeFields(tripForms.childForm)
                    ),
                    infantForm: this.filterForm(
                        journeyForms.infantForm.mergeFields(
                            tripForms.infantForm
                        )
                    ),
                };
            },
            {
                adultForm: this.createDefaultForm(PaxType.Adult),
                childForm: this.createDefaultForm(PaxType.Child),
                infantForm: this.createDefaultForm(PaxType.Infant),
            } as {
                adultForm: PassengerForm;
                childForm: PassengerForm;
                infantForm: PassengerForm;
            }
        );
    }

    override async verify(count: PassengerCount): Promise<VerifyResult> {
        try {
            const { data } = await JourneyApiApiReservePost({
                reserveRequest: {
                    ...count,
                    adultCount: count.adultCount + (count.foreignerCount || 0),
                    packageId: this.packageId,
                },
            });

            const realCount =
                count.adultCount +
                count.childCount +
                count.infantCount +
                (count.foreignerCount ?? 0);
            const { adultForm, childForm, infantForm } =
                this.createPassengerForms();

            const totalPrice =
                (count.adultCount + (count.foreignerCount || 0)) *
                    (data.adultPrice?.price || 0) +
                count.childCount * (data.childPrice?.price || 0) +
                count.infantCount * (data.infantPrice?.price || 0);

            const totalDiscount =
                (count.adultCount + (count.foreignerCount || 0)) *
                    (data.adultPrice?.discount || 0) +
                count.childCount * (data.childPrice?.discount || 0) +
                count.infantCount * (data.infantPrice?.discount || 0);
            const prices = [
                {
                    paxType: PaxType.Adult,
                    count: count.adultCount + (count.foreignerCount || 0),
                    price: data.adultPrice?.price ?? 0,
                    discount: data.adultPrice?.discount,
                },
                {
                    paxType: PaxType.Child,
                    count: count.childCount,
                    price: data.childPrice?.price ?? 0,
                    discount: data.childPrice?.discount,
                },
                {
                    paxType: PaxType.Infant,
                    count: count.infantCount ?? 0,
                    price: data.infantPrice?.price ?? 0,
                    discount: data.infantPrice?.discount,
                },
            ];
            return new VerifyResult(
                new RouteBillHandler(data?.tripReserveInfos || []),
                {
                    haveCustomDiscount: !!this.prePriceDiscountPercent,
                    paxList: [
                        ...Array(count.adultCount)
                            .fill(0)
                            .map(_ => adultForm.clone()),

                        ...Array(count.childCount)
                            .fill(0)
                            .map(_ => childForm.clone()),

                        ...Array(count.infantCount)
                            .fill(0)
                            .map(_ => infantForm.clone()),
                    ],
                    warningMessages:
                        data.tripReserveInfos?.flatMap(n =>
                            (n.warningMessages || []).map(message => ({
                                message: message || '',
                                type: 'unknown',
                            }))
                        ) || [],
                    billCode: data.billCode ?? undefined,
                    adultForm,
                    childForm,
                    infantForm,
                    billId: data.billId ?? undefined,
                    payablePrice: totalPrice - totalDiscount,
                    discount: totalDiscount,
                    totalPrice,
                    remainingTime: this.remainingTimeCalculator({
                        passengersCount: realCount,
                    }),
                    priceEntries: [
                        {
                            prices,
                        },
                    ],
                    reserveId: undefined,
                    needsDestinationMobile: false,
                    maxCapacity: this.maxCapacity,
                    disablePaxTypeValidation: true,
                }
            );
        } catch (e) {
            if (isAxiosError(e)) {
                throw new ItineraryVerifyException(
                    VerificationStatus.Cancelled,
                    e?.response?.data?.UserMessage
                );
            }
            throw e;
        }
    }

    private get discount() {
        return this.singlePayable - this.singlePrice;
    }

    override loadPreInvoice(count: {
        adultCount: number;
        childCount: number;
        infantCount: number;
    }): Promise<InvoiceEntry[]> {
        return Promise.resolve([
            ...(count.adultCount
                ? [
                      {
                          paxType: PaxType.Adult,
                          price: this.singlePayable,
                          discount: this.discount,
                          count: count.adultCount,
                          title: PaxTypeTitles[PaxType.Adult],
                      },
                  ]
                : []),
            ...(count.childCount
                ? [
                      {
                          paxType: PaxType.Child,
                          price: this.singlePayable,
                          discount: this.discount,
                          count: count.childCount,
                          title: PaxTypeTitles[PaxType.Child],
                      },
                  ]
                : []),
            ...(count.infantCount
                ? [
                      {
                          paxType: PaxType.Infant,
                          price: this.singlePayable,
                          discount: this.discount,
                          count: count.infantCount,
                          title: PaxTypeTitles[PaxType.Infant],
                      },
                  ]
                : []),
        ]);
    }

    override get prePriceDiscountPercent(): number {
        return 0;
    }

    override get netPrice(): number {
        return this.adultFare?.price ?? 0;
    }

    override get capacity(): number {
        return this.maxCapacity || 0;
    }

    override get singlePrice(): number {
        return this.netPrice;
    }

    override loadableTermsUrl = null;

    override get title(): string {
        return '';
    }

    override get totalPayableForCheck(): number | null {
        return 0;
    }

    override get totalPriceForCheck(): number | null {
        return 0;
    }

    override get descriptionItems(): string[] {
        return [];
    }
}
